"use client";

// Package Imports
import React from 'react';
import classNames from 'classnames';
import Image from 'next/image';

// Component Imports
import StylizedButtonLink from '@/components/stylizedButtonLink';

// Style Imports
import styles from "./heroDynamic.module.scss";

interface HeroDynamicProps {
  className?: string;
  icon?: string;
  header: string;
  copy?: string;
  buttonText: string;
  buttonUrl: string;
  backgroundImages: {
    mobile?: string;
    tablet?: string;
    desktop: string;
  };
}

/**
 * A component to display a hero with responsive background image, icon, header text, copy, and a button.
 *
 * @param className - Optional parent classname
 * @param icon - Icon file src
 * @param header - The header text
 * @param copy - The copy text
 * @param buttonText - Text to display on the button
 * @param buttonUrl - The url the button should link to
 * @param backgroundImages - An object containing background image URLs for different breakpoints:
 *   - mobile: URL for mobile background image (optional)
 *   - tablet: URL for tablet background image (optional)
 *   - desktop: URL for desktop background image (required)
 */

const HeroDynamic: React.FC<HeroDynamicProps> = ({ className, icon, header, copy, buttonText, buttonUrl, backgroundImages }) => {

  return (
    <section
      className={classNames(styles.hero, className && styles[className])}
      style={{
        ...(backgroundImages.mobile && { '--bg-mobile': `url(${backgroundImages.mobile})` }),
        ...(backgroundImages.tablet && { '--bg-tablet': `url(${backgroundImages.tablet})` }),
        '--bg-desktop': `url(${backgroundImages.desktop})`
      } as React.CSSProperties}
    >
      <div className={styles.heroContent}>
        {icon && (
          <Image
            src={icon}
            width={104}
            height={116}
            alt=""
          />
        )}
        <h2>{header}</h2>
        {copy && (
          <p>{copy}</p>
        )}
        <StylizedButtonLink href={buttonUrl} variant='neon-pill' size='medium'>{buttonText}</StylizedButtonLink>
      </div>
    </section>
  );
};

export default HeroDynamic;
